import React from 'react';
import { Link, useHref, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

// Things inside a row that own their click. Div-based shared controls
// (FilterSelect, MultiSelect, ListBox, FileDropzone) carry a role, not a tag;
// anything keyboard-focusable is treated as a control too. A bare
// `<div onClick>` or a cell region that must not open the row (a checkbox
// cell's padding, a strip of chips) can't be detected — mark it
// `data-row-link-ignore`. `stopPropagation()` in `onClick` alone isn't enough:
// a middle-click arrives as `auxclick`.
const ROW_CONTROLS = [
  'a', 'button', 'input', 'select', 'textarea', 'label', 'summary',
  '[contenteditable=""]', '[contenteditable="true"]', '[tabindex]:not([tabindex="-1"])',
  '[role="button"]', '[role="link"]', '[role="checkbox"]', '[role="radio"]', '[role="switch"]',
  '[role="menuitem"]', '[role="menuitemcheckbox"]', '[role="menuitemradio"]',
  '[role="option"]', '[role="combobox"]', '[role="tab"]', '[role="slider"]',
  '[role="spinbutton"]', '[role="treeitem"]', '[data-row-link-ignore]',
].join(', ');

// True when the click is the end of a text selection that touches `el`.
function endsSelectionIn(el: Element): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;
  return selection.getRangeAt(0).intersectsNode(el);
}

/**
 * A row's own link to its page (keyboard focus, hover URL, native new-tab) —
 * `DataTable` wraps its link column in one (the first, or `rowLinkColumnId`);
 * a hand-built table uses it for its title cell. Not draggable, so its text can still be selected; a click that
 * ends such a selection doesn't navigate.
 */
export function RowLink({ href, className, children }: { href?: string; className?: string; children: React.ReactNode }) {
  // No address (a list rendered without one): plain text, same styling.
  if (href == null) return <span className={className}>{children}</span>;
  return (
    <Link
      to={href}
      draggable={false}
      onClick={(e) => { if (endsSelectionIn(e.currentTarget)) e.preventDefault(); }}
      className={`rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${className ?? 'block'}`}
    >
      {children}
    </Link>
  );
}

/**
 * Click handlers for a row that opens `href` — spread onto a `<tr>` (or any
 * row element). Plain clicks go through the router; cmd/ctrl/shift/middle
 * clicks open it in a new tab. Clicks on controls in the row, inside content
 * portalled out of it (modals, hover cards), or ending a text selection don't
 * navigate. Needs a router. `DataTable`'s `getRowHref` uses this; reach for it
 * directly only in a hand-built table.
 */
export function useRowLinkHandlers(href: string) {
  const navigate = useNavigate();
  const resolvedHref = useHref(href);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    if (e.defaultPrevented) return;
    const rowEl = e.currentTarget;
    const target = e.target as Element;
    // Portalled content bubbles through the React tree but isn't inside the
    // row on the page.
    if (!rowEl.contains(target)) return;
    const control = target.closest(ROW_CONTROLS);
    if (control && rowEl.contains(control)) return;
    if (endsSelectionIn(rowEl)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      window.open(resolvedHref, '_blank', 'noopener');
      return;
    }
    navigate(href);
  };

  return {
    onClick: open,
    onAuxClick: (e: React.MouseEvent<HTMLElement>) => { if (e.button === 1) open(e); },
  };
}

/**
 * A row that opens `href`: the element form of `useRowLinkHandlers`, for rows
 * rendered inside a `.map` where a hook can't be called. Renders a `<div>` by
 * default; `as` swaps the element (`DataTable` passes its `TableRow`). Put the
 * item's name in a `RowLink` inside it so the row has a real link for the
 * keyboard and for new tabs.
 */
// The row owns its click handlers; a caller's own would be silently replaced,
// so they aren't accepted.
type LinkRowProps = { href: string; as?: React.ElementType } & Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'onAuxClick'>;

export const LinkRow = React.forwardRef<HTMLElement, LinkRowProps>(function LinkRow(
  { href, as: Component = 'div', className, ...props },
  ref,
) {
  const handlers = useRowLinkHandlers(href);
  return <Component ref={ref} {...props} className={cn('cursor-pointer', className)} {...handlers} />;
});
